import { useEffect, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";

type ArSettings = components["schemas"]["ArSettingsResponse"];

interface ArSettingsSectionProps {
  clientId: string;
}

export function ArSettingsSection({ clientId }: ArSettingsSectionProps) {
  const [settings, setSettings] = useState<ArSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .GET("/admin/clients/{clientId}/ar-settings", {
        params: { path: { clientId } },
      })
      .then(({ data, error: apiError }) => {
        if (cancelled) return;
        if (apiError) {
          setError("Failed to load AR settings.");
        } else {
          setSettings(data ?? null);
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Spinner className="size-5" />
      </div>
    );
  }
  if (error) {
    return <p className="text-destructive">{error}</p>;
  }
  if (!settings) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>AR Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-muted-foreground text-sm">
          Client-scoped accounts-receivable settings, applied to every organisation under this
          client. Read-only here; the client edits these in their own settings.
        </p>

        <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-2 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-muted-foreground">AR emails</dt>
            <dd className="font-mono">{settings.arEmailsEnabled ? "Enabled" : "Disabled"}</dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}
